/**
 * Cena serializável do Table top HAZMAT.
 *
 * Herdada do Table top do CSV Lab, sem o domínio veicular: aqui um objeto é só
 * tipo + nome + transformação, e o que for específico de um tipo (o produto
 * de um caminhão-tanque, por exemplo) entra em blocos opcionais próprios, nunca
 * em campos soltos do objeto.
 */

/** Tipos de objeto que o instrutor pode pôr na mesa. */
export const TIPOS_OBJETO_TABLETOP = [
  'caminhaoTanque',
  'carro',
  'viaturaAbt',
  'viaturaHazmat',
  'ambulancia',
  'bombeiroNivelA',
  'bombeiroNivelB',
  'bombeiroNivelC',
  'bombeiroIncendio',
  'medico',
  'vazamento',
  'liquidoDerramado',
  'fogo',
  'fogoTanque',
  'bleve',
  'curtoCircuito',
  'predio',
  'escola',
  'hospital',
  'corrego',
  'bueiro',
  'cone',
  'fita',
  'postoComando',
  'areaVitimas',
  'corredorDescontaminacao',
  'biruta',
  'vitima',
] as const

export type TipoObjetoTabletop = (typeof TIPOS_OBJETO_TABLETOP)[number]

export type VetorTabletop = readonly [number, number, number]

export interface TransformacaoTabletop {
  readonly posicao: VetorTabletop
  readonly rotacao: VetorTabletop
  readonly escala: VetorTabletop
}

export type SituacaoFonte = 'estavel' | 'vazamento' | 'tanqueEmFogo'

export const SITUACOES_FONTE: readonly SituacaoFonte[] = ['estavel', 'vazamento', 'tanqueEmFogo']

/**
 * Fonte de produto perigoso ancorada num objeto (caminhão-tanque, tanque,
 * cilindro). É daqui que as camadas calculadas — anéis de BLEVE, pluma —
 * tiram produto e quantidade. Os nomes de produto são os ids do catálogo de
 * `dominio/produtos`; este módulo não depende dele para continuar puro e
 * serializável.
 */
export interface BlocoFonteTabletop {
  readonly idProduto: string
  /** Fração de enchimento do recipiente, 0..1. */
  readonly enchimento: number
  /** O que está acontecendo com a fonte; decide que camada calculada aparece. Ausente = estável. */
  readonly situacao?: SituacaoFonte
  /** Taxa de emissão do vazamento, kg/s. */
  readonly vazaoKgS?: number
}

/**
 * Tempo na mesa — o mesmo para todas as fontes. A biruta da cena só mostra;
 * quem manda é isto, que as injeções do roteiro mudam.
 */
export interface MeteorologiaTabletop {
  readonly direcaoVentoGraus: number
  readonly ventoMS: number
  readonly periodo: 'dia' | 'noite'
  readonly ceuEncoberto: boolean
  readonly terreno: 'aberto' | 'urbano'
}

export interface ObjetoTabletop {
  readonly id: string
  readonly tipo: TipoObjetoTabletop
  readonly nome: string
  readonly transformacao: TransformacaoTabletop
  readonly cor?: string
  readonly bloqueado?: boolean
  readonly oculto?: boolean
  readonly idTanque?: string
  readonly fonte?: BlocoFonteTabletop
}

/**
 * Uma informação que a central tem no momento do acionamento. O instrutor
 * decide se ela já é confirmada ou só relato do solicitante, e se entra na
 * mensagem lida à guarnição (`visivel`) — pode revelar depois.
 */
export interface ItemAcionamento {
  readonly id: string
  readonly texto: string
  readonly confirmado: boolean
  readonly visivel: boolean
}

/**
 * Histórico inicial do acionamento, preparado junto com o cenário. Contém só
 * o que a central sabe; campo vazio fica vazio, e a mensagem diz "a confirmar"
 * em vez de inventar o dado.
 */
export interface Acionamento {
  readonly natureza: string
  readonly local: string
  /** horário simulado do chamado, livre (ex.: "14h32") */
  readonly horario: string
  readonly recursos: string
  readonly informacoes: readonly ItemAcionamento[]
}

export interface CenaTabletop {
  readonly versao: 1
  readonly nome: string
  readonly historiaInicial?: string
  readonly objetos: readonly ObjetoTabletop[]
  readonly acionamento?: Acionamento
  /** Ausente em cenas sem cálculo: vale `METEOROLOGIA_PADRAO`. */
  readonly meteorologia?: MeteorologiaTabletop
}

export type NovoObjetoTabletop = Readonly<{
  tipo: TipoObjetoTabletop
  nome?: string
  transformacao?: TransformacaoTabletop
  cor?: string
  idTanque?: string
  fonte?: BlocoFonteTabletop
}>

export type AtualizacoesObjetoTabletop = Readonly<{
  nome?: string
  cor?: string
  bloqueado?: boolean
  oculto?: boolean
  transformacao?: Partial<TransformacaoTabletop>
  /** null deixa o efeito livre, preservando a posição atual. */
  idTanque?: string | null
  /** mescla no bloco de fonte; cria o bloco se o objeto ainda não tinha */
  fonte?: Partial<BlocoFonteTabletop>
}>

export type GeradorIdTabletop = () => string
